"use client";

import { COOKIE_NAME_MATOMO_CONSENT, getMirroredCookie } from "@/lib/cookieMirror";

declare global {
    interface Window {
        _paq?: unknown[][];
    }
}

type Context = {
    customer?: string | null;
    department?: string | null;
    screen?: string | null;
};

const NEXT_PUBLIC_MATOMO_TRACKER_URL = (process.env.NEXT_PUBLIC_MATOMO_TRACKER_URL ?? "").replace(/\/+$/, "");
const MATOMO_SITE_ID = process.env.NEXT_PUBLIC_MATOMO_SITE_ID ?? "";
const MATOMO_SCRIPT_URL = process.env.NEXT_PUBLIC_MATOMO_SCRIPT_URL ?? `${NEXT_PUBLIC_MATOMO_TRACKER_URL}/matomo.js`;

let initialized = false;
let scriptInjected = false;

function getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;
    const raw = document.cookie ?? "";
    for (const part of raw.split(";")) {
        const entry = part.trim();
        if (!entry) continue;
        const idx = entry.indexOf("=");
        if (idx < 0) continue;
        const cookieName = entry.slice(0, idx).trim();
        const value = entry.slice(idx + 1).trim();
        if (cookieName !== name) continue;
        return decodeURIComponent(value);
    }
    return null;
}

function hasAcceptedMatomoConsent() {
    const matomoConsent = getCookie("cc_matomo_consent") ?? getMirroredCookie(COOKIE_NAME_MATOMO_CONSENT);
    if (matomoConsent === "rejected") return false;
    if (matomoConsent === "accepted") return true;
    return false;
}

function ensureQueue() {
    if (typeof window === "undefined") return null;
    window._paq = window._paq || [];
    return window._paq;
}

function normalizeDimension(value: string | null | undefined): string | null {
    const trimmed = (value ?? "").trim();
    if (trimmed.toLowerCase() === "unknown") return null;
    return trimmed.length > 0 ? trimmed : null;
}

function parseCustomerDepartmentFromUrlLike(pathOrUrl?: string | null) {
    const raw = (pathOrUrl ?? "").trim();
    if (!raw) return { customer: null as string | null, department: null as string | null };

    try {
        const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
        const u = new URL(raw, base);
        const fromPath = parseCustomerDepartmentFromPath(u.pathname);
        return {
            customer: normalizeDimension(fromPath.customer),
            department: normalizeDimension(fromPath.department),
        };
    } catch {
        return { customer: null as string | null, department: null as string | null };
    }
}

function resolveContext(ctx?: Context, pathHint?: string): { customer: string; department: string; screen?: string | null } {
    const contextCustomer = normalizeDimension(ctx?.customer);
    const contextDepartment = normalizeDimension(ctx?.department);

    const fromWindowPath =
        typeof window === "undefined"
            ? { customer: null as string | null, department: null as string | null }
            : parseCustomerDepartmentFromPath(window.location.pathname);
    const fromHint = parseCustomerDepartmentFromUrlLike(pathHint);
    const fromQuery =
        typeof window === "undefined"
            ? { customer: null as string | null, department: null as string | null }
            : {
                  customer: normalizeDimension(
                      new URLSearchParams(window.location.search).get("customer") ??
                          new URLSearchParams(window.location.search).get("customerSlug")
                  ),
                  department: normalizeDimension(
                      new URLSearchParams(window.location.search).get("department") ??
                          new URLSearchParams(window.location.search).get("departmentSlug")
                  ),
              };
    const pathCustomer = normalizeDimension(fromWindowPath.customer);
    const pathDepartment = normalizeDimension(fromWindowPath.department);
    const customer = contextCustomer ?? pathCustomer ?? fromHint.customer ?? fromQuery.customer ?? "unknown";
    const department = contextDepartment ?? pathDepartment ?? fromHint.department ?? fromQuery.department ?? "unknown";

    return {
        customer,
        department,
        screen: ctx?.screen ?? null,
    };
}

function setContext(ctx?: Context, pathHint?: string) {
    const q = ensureQueue();
    if (!q) return;
    const resolved = resolveContext(ctx, pathHint);

    q.push(["setCustomDimension", 1, resolved.customer]);
    q.push(["setCustomDimension", 2, resolved.department]);
    if (resolved.screen) q.push(["setCustomDimension", 3, resolved.screen]);
}

export function parseCustomerDepartmentFromPath(pathname: string) {
    const parts = pathname.split("/").filter(Boolean);
    return {
        customer: parts[0] ?? null,
        department: parts[1] ?? null,
    };
}

export function initMatomo() {
    if (!hasAcceptedMatomoConsent()) return;
    if (initialized) return;
    const q = ensureQueue();
    if (!q || !NEXT_PUBLIC_MATOMO_TRACKER_URL || !MATOMO_SITE_ID) return;

    q.push(["setTrackerUrl", `${NEXT_PUBLIC_MATOMO_TRACKER_URL}/matomo.php`]);
    q.push(["setSiteId", MATOMO_SITE_ID]);
    q.push(["setCookiePath", "/"]);
    q.push(["setConsentGiven"]);
    q.push(["rememberCookieConsentGiven"]);

    if (typeof window !== "undefined") {
        const hostname = window.location.hostname.trim();
        const hostParts = hostname.split(".").filter(Boolean);
        const isIp = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
        if (hostParts.length >= 2 && !isIp) {
            const cookieDomain = `.${hostParts.slice(-2).join(".")}`;
            q.push(["setCookieDomain", cookieDomain]);
        }
        q.push(["setSecureCookie", window.location.protocol === "https:"]);
    }

    q.push(["enableLinkTracking"]);
    q.push(["enableHeartBeatTimer", 15]);

    if (!scriptInjected) {
        const s = document.createElement("script");
        s.async = true;
        s.src = MATOMO_SCRIPT_URL;
        document.head.appendChild(s);
        scriptInjected = true;
    }

    initialized = true;
}

export function trackPageView(args: {
    path: string;
    title?: string;
    context?: Context;
}) {
    if (!hasAcceptedMatomoConsent()) return;
    const q = ensureQueue();
    if (!q) return;
    setContext(args.context, args.path);
    q.push(["setCustomUrl", args.path]);
    if (args.title) q.push(["setDocumentTitle", args.title]);
    q.push(["trackPageView"]);
}

export function trackEvent(args: {
    category: string;
    action: string;
    name?: string;
    value?: number;
    context?: Context;
}) {
    if (!hasAcceptedMatomoConsent()) return;
    const q = ensureQueue();
    if (!q) return;
    setContext(args.context);
    q.push(["trackEvent", args.category, args.action, args.name, args.value]);
}
